import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  MenuItem,
  Button,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Budget } from '../../hooks/useBudgets';
import { useTransactionCategories } from '../../hooks/useTransactionCategories';
import { authFetch } from '../../lib/authFetch';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  budget?: Budget | null;
}

const MANUAL_CATEGORY_VALUE = '__manual__';
const ALL_EXPENSES_VALUE = 'All';
const NON_EXPENSE_CATEGORY_KEYWORDS = [
  'income',
  'salary',
  'paycheck',
  'bonus',
  'deposit',
  'transfer',
  'card payment',
  'adjustment',
  'saving',
];
const isLikelyExpenseCategory = (category: string): boolean => {
  const normalized = category.trim().toLowerCase();
  if (!normalized) return false;
  return !NON_EXPENSE_CATEGORY_KEYWORDS.some((keyword) => normalized.includes(keyword));
};

const EditBudgetModal: React.FC<Props> = ({ open, onClose, onSuccess, budget }) => {
  const { categories } = useTransactionCategories({ type: 'expense' });
  const expenseCategories = useMemo(
    () => categories.filter(isLikelyExpenseCategory),
    [categories]
  );
  const selectableCategories = useMemo(
    () => [ALL_EXPENSES_VALUE, ...expenseCategories],
    [expenseCategories]
  );

  const [form, setForm] = useState({
    category: '',
    amount: 0,
    startDate: '',
    endDate: '',
    frequency: 'monthly',
  });

  const [customCategory, setCustomCategory] = useState('');

  // Rehydrate form when modal opens or when budget changes
  useEffect(() => {
    if (budget) {
      const isCustom = !selectableCategories.includes(budget.category);
      setForm({
        category: isCustom ? MANUAL_CATEGORY_VALUE : budget.category,
        amount: budget.amount || 0,
        startDate: budget.startDate?.slice(0, 10) || '',
        endDate: budget.endDate?.slice(0, 10) || '',
        frequency: budget.frequency || 'monthly',
      });
      setCustomCategory(isCustom ? budget.category : '');
    } else {
      setForm({
        category: '',
        amount: 0,
        startDate: '',
        endDate: '',
        frequency: 'monthly',
      });
      setCustomCategory('');
    }
  }, [open, budget, selectableCategories]);

  const isManualCategory = form.category === MANUAL_CATEGORY_VALUE;

  const handleCategoryChange = (value: string) => {
    setForm(f => ({
      ...f,
      category: value,
    }));

    if (value !== MANUAL_CATEGORY_VALUE) {
      setCustomCategory('');
    }
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      category: isManualCategory ? customCategory : form.category,
    };

    const method = budget ? 'PUT' : 'POST';
    const url = budget ? `/api/budgets/${budget._id}` : '/api/budgets';

    const res = await authFetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(payload),
    });

    if (res.ok) onSuccess();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {budget ? 'Edit Budget' : 'Create Budget'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          label="Category"
          value={form.category}
          onChange={e => handleCategoryChange(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="" disabled>-- Select Category --</MenuItem>
          <MenuItem value={ALL_EXPENSES_VALUE}>All Expenses</MenuItem>
          {expenseCategories.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
          <MenuItem value={MANUAL_CATEGORY_VALUE}>Other (Manual Entry)</MenuItem>
        </TextField>
        <FormHelperText sx={{ mt: -1, mb: 2 }}>
          Choose &lsquo;All Expenses&rsquo; for an overall monthly cap; use a category for a sub-budget.
        </FormHelperText>

        {isManualCategory && (
          <TextField
            fullWidth
            label="Custom Category"
            value={customCategory}
            onChange={e => setCustomCategory(e.target.value)}
            sx={{ mb: 2 }}
          />
        )}

        <TextField
          fullWidth
          label="Amount ($)"
          type="number"
          value={form.amount}
          onChange={e => setForm(f => ({ ...f, amount: Number(e.target.value) }))}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={form.startDate}
          onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={form.endDate}
          onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
          sx={{ mb: 2 }}
        />
        <TextField
          select
          fullWidth
          label="Frequency"
          value={form.frequency}
          onChange={e => setForm(f => ({ ...f, frequency: e.target.value as any }))}
        >
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </TextField>

        <Button variant="contained" fullWidth onClick={handleSubmit} sx={{ mt: 3 }}>
          {budget ? 'Update Budget' : 'Create Budget'}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default EditBudgetModal;
